import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Folder,
  FolderOpen,
  FileText,
  FileCode,
  GitBranch,
  Bot,
  Sparkles,
  ChevronRight,
  Layers,
  Database,
  User,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

interface FileNode {
  name: string
  type: 'folder' | 'file'
  icon?: React.ElementType
  color?: string
  highlight?: boolean
  aiPowered?: boolean
  humanDoc?: boolean
  description?: string
  children?: FileNode[]
}

const monorepoStructure: FileNode[] = [
  {
    name: 'project-root/',
    type: 'folder',
    children: [
      {
        name: '.claude/',
        type: 'folder',
        icon: Bot,
        color: 'text-electric-cyan',
        highlight: true,
        aiPowered: true,
        description: 'AI workspace',
        children: [
          { name: 'plans/', type: 'folder', description: 'Task plans', children: [
            { name: '<task-name>.md', type: 'file', icon: FileText },
          ]},
          { name: 'research/', type: 'folder', description: 'Context & findings' },
          { name: 'archive/', type: 'folder', description: 'Completed work' },
        ],
      },
      {
        name: 'CLAUDE.md',
        type: 'file',
        icon: Bot,
        color: 'text-electric-purple',
        highlight: true,
        aiPowered: true,
        description: 'AI docs',
      },
      {
        name: 'README.md',
        type: 'file',
        icon: FileText,
        color: 'text-aurora-teal',
        highlight: true,
        humanDoc: true,
        description: 'Human + AI docs',
      },
      {
        name: 'docker-compose.yaml',
        type: 'file',
        icon: Database,
        description: 'Services config',
      },
      {
        name: 'api/',
        type: 'folder',
        icon: Layers,
        color: 'text-electric-orange',
        description: 'Backend service',
        children: [
          { name: 'README.md', type: 'file', icon: FileText, humanDoc: true, highlight: true, color: 'text-aurora-teal' },
          { name: 'Dockerfile', type: 'file' },
          { name: 'src/', type: 'folder' },
        ],
      },
      {
        name: 'frontend/',
        type: 'folder',
        icon: Layers,
        color: 'text-electric-orange',
        description: 'Web app',
        children: [
          { name: 'README.md', type: 'file', icon: FileText, humanDoc: true, highlight: true, color: 'text-aurora-teal' },
          { name: 'Dockerfile', type: 'file' },
          { name: 'src/', type: 'folder' },
        ],
      },
    ],
  },
]

const multirepoStructure: FileNode[] = [
  {
    name: 'organization/',
    type: 'folder',
    children: [
      {
        name: 'project-api/',
        type: 'folder',
        icon: GitBranch,
        color: 'text-electric-cyan',
        description: 'Git repo',
        children: [
          { name: '.claude/', type: 'folder', icon: Bot, highlight: true, aiPowered: true, description: 'AI workspace' },
          { name: 'CLAUDE.md', type: 'file', icon: Bot, highlight: true, aiPowered: true, description: 'AI docs' },
          { name: 'README.md', type: 'file', icon: FileText, color: 'text-aurora-teal', highlight: true, humanDoc: true, description: 'Human + AI docs' },
          { name: 'Dockerfile', type: 'file' },
          { name: 'src/', type: 'folder' },
        ],
      },
      {
        name: 'project-frontend/',
        type: 'folder',
        icon: GitBranch,
        color: 'text-electric-purple',
        description: 'Git repo',
        children: [
          { name: '.claude/', type: 'folder', icon: Bot, highlight: true, aiPowered: true, description: 'AI workspace' },
          { name: 'CLAUDE.md', type: 'file', icon: Bot, highlight: true, aiPowered: true, description: 'AI docs' },
          { name: 'README.md', type: 'file', icon: FileText, color: 'text-aurora-teal', highlight: true, humanDoc: true, description: 'Human + AI docs' },
          { name: 'Dockerfile', type: 'file' },
          { name: 'src/', type: 'folder' },
        ],
      },
    ],
  },
]

const keyFiles = [
  {
    name: 'CLAUDE.md',
    icon: Bot,
    color: 'from-electric-purple to-electric-magenta',
    purpose: 'AI Docs',
    content: [
      'Code style & formatting rules',
      'Development workflow (research → plan → implement)',
      'Docker environment notes',
      'Project-specific conventions',
    ],
  },
  {
    name: '.claude/',
    icon: Sparkles,
    color: 'from-electric-cyan to-blue-500',
    purpose: 'AI Workspace',
    content: [
      'plans/ — One .md file per task',
      'research/ — Investigation findings',
      'archive/ — Completed plans',
    ],
  },
  {
    name: 'README.md',
    icon: User,
    color: 'from-aurora-teal to-teal-600',
    purpose: 'Human + AI Docs',
    content: [
      'Project overview & architecture',
      'Setup & installation instructions',
      'API documentation',
      'Contributing guidelines',
    ],
  },
]

function FileTreeNode({ node, depth = 0, expandedPaths, onToggle }: {
  node: FileNode
  depth?: number
  expandedPaths: Set<string>
  onToggle: (path: string) => void
}) {
  const path = `${depth}-${node.name}`
  const isExpanded = expandedPaths.has(path)
  const hasChildren = node.children && node.children.length > 0
  const Icon = node.icon || (node.type === 'folder' ? (isExpanded ? FolderOpen : Folder) : FileCode)

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: depth * 0.05 }}
    >
      <div
        className={`flex items-center gap-2 py-1 px-2 rounded cursor-pointer transition-all hover:bg-white/10 ${
          node.highlight
            ? node.humanDoc
              ? 'bg-aurora-teal/10'
              : 'bg-electric-cyan/10'
            : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => hasChildren && onToggle(path)}
      >
        {hasChildren && (
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="w-4 h-4 flex items-center justify-center"
          >
            <ChevronRight className="w-3 h-3 text-white/40" />
          </motion.div>
        )}
        {!hasChildren && <span className="w-4" />}
        <Icon className={`w-4 h-4 ${node.color || 'text-white/60'}`} />
        <span className={`text-sm ${
          node.highlight
            ? node.humanDoc
              ? 'text-aurora-teal font-semibold'
              : 'text-electric-cyan font-semibold'
            : 'text-white/80'
        }`}>
          {node.name}
        </span>
        {node.aiPowered && (
          <Sparkles className="w-3 h-3 text-electric-purple" />
        )}
        {node.humanDoc && (
          <>
            <User className="w-3 h-3 text-aurora-teal" />
            <Bot className="w-3 h-3 text-electric-purple" />
          </>
        )}
        {node.description && (
          <span className="text-xs text-white/40 ml-auto">{node.description}</span>
        )}
      </div>
      <AnimatePresence>
        {isExpanded && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {node.children!.map((child, i) => (
              <FileTreeNode
                key={`${child.name}-${i}`}
                node={child}
                depth={depth + 1}
                expandedPaths={expandedPaths}
                onToggle={onToggle}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function CodeStructureSlide({ isActive: _isActive }: SlideProps) {
  const [viewMode, setViewMode] = useState<'monorepo' | 'multirepo'>('monorepo')
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['0-project-root/', '0-organization/']))

  const togglePath = (path: string) => {
    const newPaths = new Set(expandedPaths)
    if (newPaths.has(path)) {
      newPaths.delete(path)
    } else {
      newPaths.add(path)
    }
    setExpandedPaths(newPaths)
  }

  const expandAll = () => {
    const allPaths = new Set<string>()
    const collectPaths = (nodes: FileNode[], depth = 0) => {
      nodes.forEach(node => {
        if (node.children) {
          allPaths.add(`${depth}-${node.name}`)
          collectPaths(node.children, depth + 1)
        }
      })
    }
    collectPaths(viewMode === 'monorepo' ? monorepoStructure : multirepoStructure)
    setExpandedPaths(allPaths)
  }

  const structure = viewMode === 'monorepo' ? monorepoStructure : multirepoStructure

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-4 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Code <span className="text-gradient">Structure</span>
            </h2>
            <p className="text-lg text-white/60">
              Organizing microservices for AI-assisted development
            </p>
          </div>
        </StaggerItem>

        {/* View mode toggle */}
        <StaggerItem>
          <div className="flex justify-center gap-3 mb-4">
            <button
              onClick={() => setViewMode('monorepo')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                viewMode === 'monorepo'
                  ? 'bg-electric-cyan/20 text-electric-cyan ring-1 ring-electric-cyan/30'
                  : 'glass text-white/60 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4 inline mr-2" />
              Monorepo
            </button>
            <button
              onClick={() => setViewMode('multirepo')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                viewMode === 'multirepo'
                  ? 'bg-electric-purple/20 text-electric-purple ring-1 ring-electric-purple/30'
                  : 'glass text-white/60 hover:text-white'
              }`}
            >
              <GitBranch className="w-4 h-4 inline mr-2" />
              Multi-repo
            </button>
            <button
              onClick={expandAll}
              className="glass px-4 py-2 rounded-full text-sm font-medium text-white/60 hover:text-white transition-all"
            >
              Expand All
            </button>
          </div>
        </StaggerItem>

        {/* Main content */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* File tree */}
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass rounded-2xl p-4 overflow-auto"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-electric-cyan" />
                  {viewMode === 'monorepo' ? 'Monorepo Structure' : 'Multi-repo Structure'}
                </h3>
                <span className="text-xs text-white/40 px-2 py-1 rounded-full bg-white/5">
                  {viewMode === 'monorepo' ? 'Single Git repo' : 'Multiple Git repos'}
                </span>
              </div>
              <div className="font-mono text-sm">
                {structure.map((node, i) => (
                  <FileTreeNode
                    key={`${node.name}-${i}`}
                    node={node}
                    expandedPaths={expandedPaths}
                    onToggle={togglePath}
                  />
                ))}
              </div>
            </motion.div>

            {/* Key files explanation */}
            <div className="flex flex-col gap-3">
              {keyFiles.map((file, index) => {
                const Icon = file.icon
                return (
                  <motion.div
                    key={file.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    className="glass rounded-xl p-4 cursor-pointer transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${file.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-lg font-bold text-white">{file.name}</code>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                            {file.purpose}
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {file.content.map((item, i) => (
                            <li key={i} className="text-sm text-white/60 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-electric-cyan/60" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </StaggerItem>

        {/* Bottom insight */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-3 mb-[100px]"
          >
            <div className="glass rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-electric-purple flex-shrink-0" />
                <div>
                  <p className="text-sm text-white/70">
                    <span className="text-electric-cyan font-semibold">Pro tip:</span>{' '}
                    Each microservice gets its own <code className="text-electric-purple">CLAUDE.md</code> with service-specific instructions.
                    Share common patterns via a root-level file in monorepos.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
